import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { shouldUseDemoMode } from '../utils/demoMode';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logger } from '../utils/logger';
import { 
  addMessage, 
  incrementUnreadCount, 
  updateConversationLastMessage, 
  updateOnlineUsers, 
  addOnlineUser, 
  removeOnlineUser,
  addNewConversation,
  fetchConversations
} from '../store/slices/chatSlice';
import { addNotification, incrementUnreadCount as incrementNotificationCount } from '../store/slices/notificationSlice';

const getSocketUrl = () => {
  try {
    // Priority 1: Use VITE_SOCKET_URL if provided
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SOCKET_URL) {
      return import.meta.env.VITE_SOCKET_URL;
    }
    // Priority 2: Use VITE_BACKEND_URL without /api suffix
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) {
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      // Remove /api suffix if present
      return backendUrl.replace(/\/api$/, '');
    }
  } catch (error) {
    // Ignore
  }
  return 'http://localhost:5001';
};

const getBackendUrl = () => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) {
      return import.meta.env.VITE_BACKEND_URL;
    }
  } catch (error) {
    // Ignore
  }
  return 'http://localhost:5001';
};

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  sendTyping: (chatId: string) => void;
  stopTyping: (chatId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinChat: () => {},
  leaveChat: () => {},
  sendTyping: () => {},
  stopTyping: () => {},
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider = ({ children }: SocketProviderProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const currentConversation = useAppSelector((state) => state.chat.currentConversation);
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null); // 🔥 Added state for socket
  const currentConversationRef = useRef(currentConversation);
  
  // Update ref whenever currentConversation changes
  useEffect(() => {
    currentConversationRef.current = currentConversation;
  }, [currentConversation]);

  useEffect(() => {
    // Don't initialize socket if no user
    if (!user || !user._id) {
      logger.log('⏭️  [SocketProvider] Skipping socket initialization - no user');
      return;
    }

    // Don't initialize socket in demo mode
    if (shouldUseDemoMode()) {
      logger.log('⏭️  [SocketProvider] Skipping socket initialization - Demo Mode active');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      logger.warn('⚠️  [SocketProvider] No token found for socket authentication');
      return;
    }
    
    // Prevent duplicate connections
    if (socketRef.current?.connected) {
      logger.log('⏭️  [SocketProvider] Socket already connected - skipping initialization');
      return;
    }

    // Get current backend URL (may have changed via environment config)
    const BACKEND_URL = getBackendUrl();
    const SOCKET_URL = getSocketUrl();
    logger.log('🔌 [SocketProvider] Initializing Socket.IO connection');
    logger.log('🔌 [SocketProvider] Backend URL:', BACKEND_URL);
    logger.log('🔌 [SocketProvider] Socket URL:', SOCKET_URL);
    logger.log('🔌 [SocketProvider] User ID:', user._id);
    logger.log('🔌 [SocketProvider] Token available:', !!token);

    // Create socket connection with authentication
    const newSocket = io(SOCKET_URL, {
      auth: {
        token: token
      },
      transports: ['polling', 'websocket'], // Use polling first to avoid Node.js module issues
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 10000,
      forceNew: true
    });

    socketRef.current = newSocket;
    setSocket(newSocket); // 🔥 Set socket state

    // Connection handlers
    newSocket.on('connect', () => {
      logger.log('✅ [SocketProvider] Socket connected - ID:', newSocket.id);
      logger.log('✅ [SocketProvider] User ID:', user?._id);
      setIsConnected(true);
      
      // 🔥 EMIT: Join user's personal room for direct notifications
      if (user?._id) {
        newSocket.emit('join_user_room', { userId: user._id });
        logger.log('🚪 [SocketProvider] Emitted join_user_room:', user._id);
        logger.log('🚪 [SocketProvider] Should be joining room: user:' + user._id);
      } else {
        logger.warn('⚠️  [SocketProvider] Cannot join user room - no user._id');
      }
      
      // 🔥 EMIT: Request current online users
      newSocket.emit('get_online_users');
      logger.log('📡 [SocketProvider] Emitted get_online_users');
      
      // 🔥 EMIT: Broadcast that we're online
      newSocket.emit('user_connected', { userId: user._id });
      logger.log('🟢 [SocketProvider] Emitted user_connected:', user._id);
    });

    // Listen for user room joined confirmation
    newSocket.on('user_room_joined', ({ userId, room }) => {
      logger.log('✅✅✅ [SocketProvider] USER ROOM JOINED CONFIRMATION');
      logger.log('✅ [SocketProvider] User ID:', userId);
      logger.log('✅ [SocketProvider] Room:', room);
      logger.log('✅✅✅ [SocketProvider] Ready to receive calls globally!');
    });

    newSocket.on('disconnect', (reason) => {
      logger.log('❌ [SocketProvider] Socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      logger.log('⚠️  [SocketProvider] Socket connection error:', error.message);
    });

    newSocket.on('authentication_error', (data) => {
      logger.error('❌ [SocketProvider] Socket authentication error:', data);
      if (!shouldUseDemoMode()) {
        toast.error('Chat authentication failed', { description: 'Please try logging in again' });
      }
    });

    // Handle blocking errors
    newSocket.on('error', (data: Record<string, unknown>) => {
      logger.error('❌ [SocketProvider] Socket error:', data);
      
      // Check for blocking-related errors
      if (data.code === 'USER_BLOCKED') {
        toast.error('Message not sent', { 
          description: 'You cannot send messages to this user. They may have blocked you.' 
        });
      } else if (data.code === 'YOU_BLOCKED_USER') {
        toast.error('Message not sent', { 
          description: 'You have blocked this user. Unblock them to send messages.' 
        });
      } else if (data.code === 'NO_SUBSCRIPTION' || data.code === 'SUBSCRIPTION_EXPIRED') {
        toast.error('Subscription Required', { 
          description: data.message as string || 'Please subscribe to send messages.' 
        });
      } else if (data.message) {
        toast.error('Error', { description: data.message as string });
      }
    });

    // 🔥 Helper function to handle new messages (avoid code duplication)
    const handleNewMessage = (message: Record<string, unknown>, eventName: string) => {
      logger.log('🔥🔥🔥 ============================================');
      logger.log(`📨 [SocketProvider] [${eventName}] NEW MESSAGE RECEIVED`);
      logger.log('🔥🔥🔥 ============================================');
      logger.log('📨 Message Details:', {
        chatId: message.chatId,
        senderId: message.senderId,
        content: message.content,
        timestamp: new Date().toISOString()
      });
      
      // Use ref to get latest currentConversation value
      const activeConversation = currentConversationRef.current;
      logger.log('📨 [SocketProvider] Current active conversation:', activeConversation?._id);
      logger.log('📨 [SocketProvider] Message chatId:', message.chatId);
      
      // Get sender ID
      const messageSenderId = typeof message.senderId === 'object' && message.senderId?._id 
        ? message.senderId._id 
        : message.senderId;
      
      const isFromCurrentUser = String(messageSenderId) === String(user._id);
      logger.log('📨 [SocketProvider] Is from current user?', isFromCurrentUser);
      
      // Update conversation list for ALL messages (both sent and received)
      dispatch(updateConversationLastMessage({ 
        conversationId: message.chatId, 
        message: message 
      }));
      
      // ⚠️ IMPORTANT: Skip adding message if it's from current user
      // Messages from current user are handled via optimistic updates + API response
      if (isFromCurrentUser) {
        logger.log('⏭️  [SocketProvider] Skipping addMessage - from current user (handled via optimistic update)');
        return;
      }
      
      // Only add message if it's for the current conversation OR from another user
      if (activeConversation && message.chatId === activeConversation._id) {
        logger.log('✅ [SocketProvider] Message is for active conversation - adding to Redux (NO unread increment)');
        dispatch(addMessage(message));
        
        logger.log('🔔 [SocketProvider] Playing notification sound - message from other user');
        // Play notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZizcIGWi77eefTRAMUKfj8LZjHAY4ktfzzX0sBSR3x/DdkEAKFF606eunVRQKRp/g8r5sIQUrgs7y2Ik3CBlou+3nn00QDFCn4/C2YxwGOJLX88l9LAUkd8fw3ZBAChRetOnrp1UUCkaf4PK+bCEFK4LO8tmJNwgZaLvt559NEAxQp+PwtmMcBjiS1/PJfSwFJHfH8N2QQAoUXrTp66dVFApGn+Dyvmw=');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } catch {
          // Ignore audio errors
        }
      } else {
        logger.log('📬 [SocketProvider] Message is for different conversation - incrementing unread count');
        // Message for different conversation - increment unread and update last message
        dispatch(incrementUnreadCount(message.chatId));
        logger.log('✅ [SocketProvider] Incremented unread count for conversation:', message.chatId);
      }
    };

    // Real-time message handlers - listen for multiple event names
    newSocket.on('new_message', (message: any) => {
      handleNewMessage(message, 'new_message');
    });
    
    newSocket.on('message', (message: any) => {
      handleNewMessage(message, 'message');
    });
    
    newSocket.on('receiveMessage', (message: any) => {
      handleNewMessage(message, 'receiveMessage');
    });
    
    logger.log('✅ [SocketProvider] Socket event listeners registered');

    // Typing indicators
    newSocket.on('user_typing', ({ userId, chatId }: { userId: string; chatId: string }) => {
      logger.log('⌨️  [SocketProvider] User typing:', userId, 'in chat:', chatId);
    });

    newSocket.on('user_stopped_typing', ({ userId, chatId }: { userId: string; chatId: string }) => {
      logger.log('⌨️  [SocketProvider] User stopped typing:', userId, 'in chat:', chatId);
    });

    // Online status
    newSocket.on('user_online', ({ userId }: { userId: string }) => {
      logger.log('🟢 [SocketProvider] User online:', userId);
      dispatch(addOnlineUser(userId));
    });

    newSocket.on('user_offline', ({ userId }: { userId: string }) => {
      logger.log('🔴 [SocketProvider] User offline:', userId);
      dispatch(removeOnlineUser(userId));
    });

    newSocket.on('online_users', ({ userIds }: { userIds: string[] }) => {
      logger.log('👥 [SocketProvider] Online users updated:', userIds.length, 'users');
      dispatch(updateOnlineUsers(userIds));
    });

    // Messages read
    newSocket.on('messages_read', ({ userId, chatId }: { userId: string; chatId: string }) => {
      logger.log('✅ [SocketProvider] Messages read by:', userId, 'in chat:', chatId);
    });

    // Message deleted
    newSocket.on('message_deleted', ({ chatId, messageId }: { chatId: string; messageId: string }) => {
      logger.log('🗑️  [SocketProvider] Message deleted:', messageId, 'in chat:', chatId);
    });

    // New notification received (for offline users who received messages)
    newSocket.on('new_notification', (notification: any) => {
      logger.log('🔔 [SocketProvider] New notification received:', notification);
      
      // Add to notification store
      dispatch(addNotification(notification));
      dispatch(incrementNotificationCount());
      
      // Show toast notification
      if (notification.type === 'message') {
        toast.info(notification.title, {
          description: notification.message,
          duration: 5000,
        });
      }
    });

    // New chat created (when interest accepted)
    newSocket.on('new_chat', (data: any) => {
      logger.log('🔥🔥🔥 ============================================');
      logger.log('💬 [SocketProvider] NEW CHAT EVENT RECEIVED');
      logger.log('🔥🔥🔥 ============================================');
      logger.log('💬 [SocketProvider] Full data:', JSON.stringify(data, null, 2));
      logger.log('💬 [SocketProvider] Has chat data?', !!data.chat);
      logger.log('💬 [SocketProvider] Chat ID:', data.chat?._id);
      logger.log('💬 [SocketProvider] Participants:', data.chat?.participants?.length);
      
      // Show toast notification
      toast.success('New Chat!', {
        description: data.message,
        duration: 5000,
      });
      
      // Add the new chat to Redux store if chat data is provided
      if (data.chat) {
        logger.log('✅ [SocketProvider] Adding chat to Redux store:', data.chat._id);
        logger.log('✅ [SocketProvider] Chat object:', data.chat);
        dispatch(addNewConversation(data.chat));
        logger.log('✅ [SocketProvider] Chat added to Redux successfully');
        logger.log('✅ [SocketProvider] No need to refetch - chat already added to state');
      } else {
        logger.error('❌ [SocketProvider] No chat data in new_chat event!');
        // If no chat data provided, force refresh to get it
        logger.log('💬 [SocketProvider] Fetching conversations to get new chat...');
        dispatch(fetchConversations(true));
      }
    });

    // 📞 IMPORTANT: Listen for call-signal events in SocketProvider
    // This ensures call signals are captured even if WebRTCProvider hasn't loaded yet
    newSocket.on('call-signal', (signal: any) => {
      logger.log('📞🔥 [SocketProvider] CALL-SIGNAL RECEIVED:', signal.type);
      logger.log('📞🔥 [SocketProvider] Signal details:', signal);
      
      // The WebRTCService will also receive this via its own listener
      // This is just for logging and ensuring the signal is received
      if (signal.type === 'call-request') {
        logger.log('📞🔥 [SocketProvider] INCOMING CALL from:', signal.fromName);
        logger.log('📞🔥 [SocketProvider] Room ID:', signal.roomId);
        logger.log('📞🔥 [SocketProvider] From user:', signal.from);
      }
    });

    // 📱 Listen for REQUEST_CONTACTS_UPLOAD event from backend
    newSocket.on('REQUEST_CONTACTS_UPLOAD', (data: any) => {
      logger.log('📱 [SocketProvider] REQUEST_CONTACTS_UPLOAD received from backend');
      logger.log('📱 [SocketProvider] Data:', data);
      
      // Forward to Expo app via postMessage
      if (window.ReactNativeWebView) {
        logger.log('📱 [SocketProvider] Forwarding REQUEST_CONTACTS_UPLOAD to Expo app...');
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'REQUEST_CONTACTS_UPLOAD',
          token: data.token,
          message: data.message
        }));
      } else {
        logger.log('⚠️ [SocketProvider] Not in Expo app - skipping contacts upload request');
      }
    });

    // Cleanup on unmount
    return () => {
      logger.log('🔌 [SocketProvider] Disconnecting socket...');
      
      // Remove all event listeners before disconnecting
      newSocket.off('new_message');
      newSocket.off('message');
      newSocket.off('receiveMessage');
      newSocket.off('user_typing');
      newSocket.off('user_stopped_typing');
      newSocket.off('user_online');
      newSocket.off('user_offline');
      newSocket.off('online_users');
      newSocket.off('messages_read');
      newSocket.off('message_deleted');
      newSocket.off('new_notification');
      newSocket.off('new_chat');
      newSocket.off('call-signal');
      newSocket.off('REQUEST_CONTACTS_UPLOAD');
      newSocket.off('user_room_joined');
      newSocket.off('connect');
      newSocket.off('disconnect');
      newSocket.off('connect_error');
      newSocket.off('authentication_error');
      
      newSocket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [user, dispatch]);

  // Helper functions
  const joinChat = (chatId: string) => {
    logger.log('🚪 [SocketProvider] joinChat called for:', chatId);
    
    if (socketRef.current && isConnected) {
      logger.log('✅ [SocketProvider] Emitting join_chat event for:', chatId);
      socketRef.current.emit('join_chat', { chatId });
    } else {
      logger.warn('⚠️  [SocketProvider] Cannot join chat - socket not ready:', {
        hasSocket: !!socketRef.current,
        isConnected: isConnected
      });
    }
  };

  const leaveChat = (chatId: string) => {
    logger.log('🚪 [SocketProvider] leaveChat called for:', chatId);
    
    if (socketRef.current && isConnected) {
      logger.log('✅ [SocketProvider] Emitting leave_chat event for:', chatId);
      socketRef.current.emit('leave_chat', { chatId });
    }
  };

  const sendTyping = (chatId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('typing_start', { chatId });
    }
  };

  const stopTyping = (chatId: string) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('typing_stop', { chatId });
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket: socket,
        isConnected: isConnected,
        joinChat,
        leaveChat,
        sendTyping,
        stopTyping,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};